import {ArrowLeft, ArrowRight} from 'lucide-react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Switch} from '@common-web/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {WizardState} from './types';
import {WIZARD_STEPS} from './utils';

export function StepGroupInfo({
  state,
  onUpdate,
  clientId,
  onClientIdChange,
  clients,
  showClientSelector,
  isValid,
  onBack,
  onNext,
}: {
  state: WizardState;
  onUpdate: (partial: Partial<WizardState>) => void;
  clientId: string;
  onClientIdChange: (id: string) => void;
  clients: RouterOutput['internal']['client']['list'];
  showClientSelector: boolean;
  isValid: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const billingCyclesQuery = trpc.vba.groups.groupDetails.getBillingCycles.useQuery();

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardContent className='p-6'>
        <h3 className='text-lg font-semibold text-brand-navy mb-1'>{WIZARD_STEPS[1].label}</h3>
        <p className='text-sm text-muted-foreground mb-6'>
          Enter the company details for the new group.
        </p>

        {showClientSelector && (
          <>
            <div className='space-y-2'>
              <Label>Client *</Label>
              <Select value={clientId} onValueChange={onClientIdChange}>
                <SelectTrigger>
                  <SelectValue placeholder='Select a client' />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='border-t mt-5 pt-5' />
          </>
        )}

        <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3'>
          Company
        </p>
        <div className='grid grid-cols-2 gap-3'>
          <div className='space-y-2'>
            <Label>Group Name *</Label>
            <Input
              value={state.groupName}
              onChange={(e) => onUpdate({groupName: e.target.value})}
              placeholder='Company name'
            />
          </div>
          <div className='space-y-2'>
            <Label>FEIN</Label>
            <Input
              value={state.federalTaxId}
              onChange={(e) => onUpdate({federalTaxId: e.target.value})}
              placeholder='123456789'
            />
          </div>
        </div>

        <div className='border-t mt-5 pt-5'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3'>
            Address
          </p>
          <div className='space-y-3'>
            <div className='space-y-2'>
              <Label>Address Line 1 *</Label>
              <Input
                value={state.addressLine1}
                onChange={(e) => onUpdate({addressLine1: e.target.value})}
              />
            </div>
            <div className='space-y-2'>
              <Label>Address Line 2</Label>
              <Input
                value={state.addressLine2}
                onChange={(e) => onUpdate({addressLine2: e.target.value})}
              />
            </div>
            <div className='grid grid-cols-3 gap-3'>
              <div className='space-y-2'>
                <Label>City *</Label>
                <Input value={state.city} onChange={(e) => onUpdate({city: e.target.value})} />
              </div>
              <div className='space-y-2'>
                <Label>State *</Label>
                <Input
                  value={state.stateCode}
                  onChange={(e) => onUpdate({stateCode: e.target.value})}
                  maxLength={2}
                  placeholder='CA'
                />
              </div>
              <div className='space-y-2'>
                <Label>ZIP</Label>
                <Input
                  value={state.zipCode}
                  onChange={(e) => onUpdate({zipCode: e.target.value})}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='border-t mt-5 pt-5'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3'>
            Primary Contact
          </p>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-2'>
              <Label>First Name *</Label>
              <Input
                value={state.firstName}
                onChange={(e) => onUpdate({firstName: e.target.value})}
              />
            </div>
            <div className='space-y-2'>
              <Label>Last Name *</Label>
              <Input
                value={state.lastName}
                onChange={(e) => onUpdate({lastName: e.target.value})}
              />
            </div>
            <div className='space-y-2'>
              <Label>Phone *</Label>
              <Input value={state.phone} onChange={(e) => onUpdate({phone: e.target.value})} />
            </div>
            <div className='space-y-2'>
              <Label>Email *</Label>
              <Input
                type='email'
                value={state.email}
                onChange={(e) => onUpdate({email: e.target.value})}
              />
            </div>
          </div>
        </div>

        <div className='border-t mt-5 pt-5'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3'>
            Dates & Options
          </p>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-2'>
              <Label>Original / Effective Date *</Label>
              <Input
                type='date'
                value={state.originalDate}
                onChange={(e) => onUpdate({originalDate: e.target.value})}
              />
            </div>
            <div className='space-y-2'>
              <Label>Open Enrollment Date</Label>
              <Input
                type='date'
                value={state.openEnrollmentDate}
                onChange={(e) => onUpdate({openEnrollmentDate: e.target.value})}
              />
            </div>
            <div className='space-y-2'>
              <Label>Billing Cycle *</Label>
              <Select
                value={state.billingCycleId}
                onValueChange={(v) => onUpdate({billingCycleId: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(billingCyclesQuery.data ?? []).map(
                    (cycle: {id: string; description: string}) => (
                      <SelectItem key={cycle.id} value={cycle.id}>
                        {cycle.description}
                      </SelectItem>
                    ),
                  )}
                  {!billingCyclesQuery.data && (
                    <>
                      <SelectItem value='MO'>Monthly</SelectItem>
                      <SelectItem value='WK'>Weekly</SelectItem>
                      <SelectItem value='BW'>Bi-Weekly</SelectItem>
                      <SelectItem value='SM'>Semi-Monthly</SelectItem>
                      <SelectItem value='WE'>Weekly (48)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className='flex items-center gap-3 mt-4'>
            <Switch
              checked={state.isAnalyzerGroup}
              onCheckedChange={(v) => onUpdate({isAnalyzerGroup: v})}
            />
            <Label>Analyzer Group</Label>
          </div>
        </div>

        <div className='flex items-center justify-between mt-6 pt-4 border-t'>
          <Button variant='outline' onClick={onBack}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back
          </Button>
          <Button onClick={onNext} disabled={!isValid}>
            Continue
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
